"use client";

import React from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Neighborhood {
  id: string;
  name: string;
  focus: string;
  description: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const neighborhoods: Neighborhood[] = [{
  id: "brookhaven",
  name: "Brookhaven",
  focus: "Historic Estates & Whole-Home Renovations",
  description: "Full-scale interior transformations, expansive kitchen additions, and high-end finishes tailored to classic Brookhaven properties.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_brookhaven_historic.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_brookhaven_historic.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Renovated stately brick home with manicured landscape in Brookhaven"
  }
}, {
  id: "dekalb-county",
  name: "DeKalb County",
  focus: "Custom Residential Remodeling",
  description: "Comprehensive design-build renovations, structural alterations, and modern living expansions throughout the greater DeKalb County area.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_dekalb_county.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_dekalb_county.webp-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Modern residential architectural renovation in DeKalb County"
  }
}, {
  id: "ashford-park",
  name: "Ashford Park",
  focus: "Gourmet Kitchens & Living Spaces",
  description: "Open-concept kitchen remodels, custom pantry cabinetry, and seamless flow upgrades crafted for Ashford Park families.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_ashford_park.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_ashford_park.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Spacious luxury craftsman home exterior in Ashford Park"
  }
}, {
  id: "brookhaven-heights",
  name: "Brookhaven Heights",
  focus: "Primary Suites & Luxury Bathrooms",
  description: "Spa-grade bathroom suites, bespoke vanities, and primary suite overhauls across quiet Brookhaven Heights avenues.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_brookhaven_heights.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_brookhaven_heights.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Charming renovated cottage home exterior in Brookhaven Heights"
  }
}, {
  id: "drew-valley",
  name: "Drew Valley",
  focus: "Modern Additions & Layout Reconfigurations",
  description: "Second-story additions, square-footage expansions, and contemporary floor plan updates for mid-century Drew Valley residences.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_drew_valley.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_drew_valley.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Expanded single-story home with fresh modern siding in Drew Valley"
  }
}, {
  id: "lynwood-park",
  name: "Lynwood Park",
  focus: "Finished Basements & Outdoor Living",
  description: "Entertainment lower levels, walk-out terraces, covered porches, and outdoor kitchens built to enrich Lynwood Park properties.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_lynwood_park.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_lynwood_park.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Contemporary home with attached timber porch in Lynwood Park"
  }
}, {
  id: "sexton-woods",
  name: "Sexton Woods",
  focus: "Architectural Extensions & Renovations",
  description: "Meticulous interior design-build craft, mudrooms, and custom architectural enhancements throughout Sexton Woods.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_sexton_woods.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/service_area_sexton_woods.webp-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Renovated traditional home surrounded by mature trees in Sexton Woods"
  }
}];
export default function ServiceAreaIntro() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    once: true
  });
  return <section id="serviceareaintro" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 border-b border-border transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Introduction */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16">
          <div className="lg:col-span-8 space-y-4">
            <div className="inline-flex items-center gap-2 text-primary font-medium text-sm tracking-wide">
              <MapPin className="size-4 text-primary" aria-hidden="true" />
              <span>Brookhaven, GA & DeKalb County Service Communities</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
              Locally rooted renovations across Brookhaven and DeKalb County
            </h1>
            <p className="text-muted-foreground text-base sm:text-lg max-w-2xl leading-relaxed">
              Brookhaven Elevation Renovations delivers design-build residential remodeling across Brookhaven, GA and surrounding communities. From historical restorations to contemporary extensions, we bring high-craftsmanship kitchen, bathroom, whole-home, and exterior transformations to seven distinct neighborhoods.
            </p>
          </div>
          <div className="lg:col-span-4 lg:flex lg:justify-end">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactRequestAnEstimatePage">
                Request an estimate
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Neighborhoods Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {neighborhoods.map((item, i) => <Card key={item.id} data-index={i} className="group overflow-hidden rounded-xl border border-border bg-card shadow-xs hover:shadow-md transition-shadow duration-200">
              <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute top-3 left-3 bg-secondary/90 text-secondary-foreground px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1.5 shadow-xs">
                  <MapPin className="size-3 text-primary" aria-hidden="true" />
                  <span>{item.name}</span>
                </div>
              </div>

              <CardContent className="p-6 space-y-3">
                <h3 className="font-serif text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors">
                  {item.name}
                </h3>
                <p className="text-xs font-medium uppercase tracking-wider text-primary">
                  {item.focus}
                </p>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {item.description}
                </p>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}